import React, { useEffect, useState } from 'react';

import useProductPrice, { getSKUByItemId, getPriceAriaLabel, useMSRPProps } from './useProductPrice';
import useModelPrice from './useModelPrice';
import { useProductInitial } from './useProductProps';
import { withError } from '../shared/components/error-boundary';
import { Helpers } from '../core';

const MAX_INTERVAL = 100;

/***
 * Returns ETR html structure for price elements
 */
const getPriceHtml = price => {
    if (!price || (!price.salePrice && !price.regularPrice)) {
        return null;
    }
    const { translations, msrp } = price;
    const { salePriceSuffix } = translations || {};
    const showStrike = price.salePrice < price.regularPrice;
    const { TooltipProps, buttonProps } = msrp || {};
    const { title, description } = TooltipProps || {};
    const { children: msrpLabel } = buttonProps || {};
    const tooltipId = `msrp-tooltip-${price.itemId}`;
    const msrpText = msrp
        ? `<span class='price_msrp'>
                <input class='msrp-tooltip-ctrl' type="checkbox" id="${tooltipId}"/>
                <label for=${tooltipId}>
                    <span class='msrp-label'>${msrpLabel}</span>
                    <span class='msrp-tooltip-container'>
                        <span class='cust-msrp-tooltip' style="display:none">
                            <span class="msrp-tooltip-close" type="button" aria-label="Close tooltip">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" height="16" width="16" data-test-hook="@hpi-sox/hpstellar-core/icon/Cross" aria-hidden="true"><g><path d="m9.41 8 4.29-4.29a1 1 0 0 0-1.41-1.41L8 6.59 3.71 2.3A1 1 0 0 0 2.3 3.71L6.59 8 2.3 12.29a1 1 0 0 0 0 1.41 1 1 0 0 0 .7.3 1 1 0 0 0 .7-.29L8 9.41l4.29 4.29a1 1 0 0 0 .71.3 1 1 0 0 0 .71-.29 1 1 0 0 0 0-1.41Z"></path></g></svg>
                            </span>
                            <span class='msrp-tooltip-content'>${description}</span>
                        </span>
                    </span>
                </label>
            </span>`
        : '';

    return `<span class='price-info'>${
        ['CTO', 'category'].includes(price.productType)
            ? msrp
                ? `<span class="price_text" aria-hidden="true">Starting ${msrpText}</span>`
                : '<span id="price_text" aria-hidden="true" style=""> Starting at </span>'
            : msrp
              ? msrpText
              : ''
    }
    ${
        showStrike
            ? `<span class="price_strike" aria-hidden="true" style="text-decoration: line-through;">${Helpers.formatCurrency(
                  price.regularPrice,
                  '$',
                  true,
              )}</span>`
            : ''
    }
    </span>
    <span class="price_amount" aria-hidden="true">${Helpers.formatCurrency(price.salePrice, '$', true)}${salePriceSuffix || ''}</span>`;
};

/***
 * Checks DOM for price elements. Use this for custom html that has these price blocks in them. This is primarily to support
 * the existing ETR functionality that allows content team to input price elements that will be auto populated on page load
 * @name useDynamicPriceHtml
 * @param {[]} dependencies - List of dependencies that should trigger a re-check of pricing
 * @param options - Options object
 * @param options.selectorPrefix Optional css selector prefix used to isolate scanning of DOM
 * @param options.timeout The interval for polling. If it's <= 0 it will not poll
 * @param options.maxAttempts max attempts for polling
 */
export default (dependencies = [], options) => {
    const [elementPrices, setElementPrice] = useState([]);
    const [attempts, setAttempts] = useState(0);
    const [modelElementPrices, setModelElementPrice] = useState([]);
    const { prices } = useProductPrice(elementPrices, { getByItemId: true });
    const { modelPrices } = useModelPrice(modelElementPrices);
    const msrp = useMSRPProps();
    let { selectorPrefix, timeout, maxAttempts = MAX_INTERVAL } = options || {};
    selectorPrefix = selectorPrefix ? `${selectorPrefix} ` : '';
    //subscription price is only available from pdp product initial data
    const productInitial = useProductInitial();
    //set up polling for content that comes in async or is lazy loaded
    useEffect(() => {
        let interval;
        if (timeout > 0) {
            interval = setInterval(() => {
                setAttempts(attempts => {
                    const nextAttempts = attempts + 1;
                    if (nextAttempts > maxAttempts) {
                        clearInterval(interval);
                    }
                    return nextAttempts;
                });
            }, timeout);
        }
        return () => clearInterval(interval);
    }, [timeout, maxAttempts]);

    useEffect(() => {
        try {
            if (typeof document === 'undefined' || attempts > maxAttempts) {
                return () => {};
            }
            //Scan DOM for elements with the data-omni
            const priceNodes = [
                ...(document.querySelectorAll(
                    `${selectorPrefix}[id^="price_holder"][data-omni]:not([data-price]):not([data-price-set])`,
                ) || []),
            ].reduce((r, e) => {
                try {
                    const data = JSON.parse(e.getAttribute('data-omni'));
                    const sku = getSKUByItemId(data.itemId, prices);
                    if (elementPrices.indexOf(e => e.itemId === data.itemId) < 0 && !(sku in prices)) {
                        r.push(data);
                        //mark item element for price fetch
                        e.setAttribute('data-price', data.itemId);
                    }
                } catch (e) {}
                return r;
            }, []);

            //Scan DOM for elements with the data-ui and model price
            const modelPriceNodes = [
                ...(document.querySelectorAll(
                    `${selectorPrefix}[id^="price_holder_model"][data-ui]:not([data-modal-price]):not([data-price-set])`,
                ) || []),
            ].reduce((r, e) => {
                try {
                    const data = JSON.parse(e.getAttribute('data-ui'));
                    const { modelId } = data || {};
                    if (modelElementPrices.indexOf(e => e.modelId === data.modelId) < 0 && !(modelId in modelPrices)) {
                        r.push(data);
                        //mark item element for price fetch
                        e.setAttribute('data-model-price', data.modelId);
                    }
                } catch (e) {
                    //
                }
                return r;
            }, []);
            //if new price elements discovered, update list of price elements
            if (priceNodes.length > 0) {
                setElementPrice([...elementPrices, ...priceNodes]);
            }
            if (modelPriceNodes.length > 0) {
                setModelElementPrice([...modelElementPrices, ...modelPriceNodes]);
            }
            //if we have received prices, check for elements we marked to set price
            if (Object.keys(prices).length > 0) {
                [...(document.querySelectorAll(`${selectorPrefix}[data-price]:not([data-price-set])`) || [])].forEach(
                    e => {
                        const itemId = e.getAttribute('data-price');
                        const sku = getSKUByItemId(itemId, prices);
                        if (sku && prices[sku]) {
                            const priceObj = prices[sku];
                            e.setAttribute('aria-label', getPriceAriaLabel(priceObj));
                            e.classList.add('standard_price');
                            e.setAttribute('data-price-set', true);
                            e.innerHTML = Helpers.purifyHTML(getPriceHtml(priceObj));
                        }
                    },
                );
            }
            //if we have received prices, check for elements we marked to set price
            if (Object.keys(modelPrices).length > 0) {
                [
                    ...(document.querySelectorAll(`${selectorPrefix}[data-model-price]:not([data-price-set])`) || []),
                ].forEach(e => {
                    const modelId = e.getAttribute('data-model-price');
                    if (modelId && modelPrices[modelId]) {
                        const priceObj = modelPrices[modelId];
                        e.setAttribute('aria-label', getPriceAriaLabel(priceObj));
                        e.classList.add('standard_price');
                        e.setAttribute('data-price-set', true);
                        e.innerHTML = Helpers.purifyHTML(getPriceHtml(priceObj));
                    }
                });
            }
            //if on the pdp check for sub price elements
            if (productInitial?.enrollmentKit?.price) {
                const { catentryId, enrollmentKit } = productInitial;
                const { price } = enrollmentKit;
                const priceObj = { msrp, ...price };
                document
                    .querySelectorAll(`${selectorPrefix}[id^="sub_price_holder_product_${catentryId}"]`)
                    .forEach(e => {
                        if (e.innerHTML) {
                            return;
                        }
                        e.innerHTML = Helpers.purifyHTML(getPriceHtml(priceObj));
                        e.setAttribute('aria-label', getPriceAriaLabel(priceObj));
                    });
            }
        } catch (e) {}
    }, [...dependencies, prices, modelPrices, attempts]);
};
